import { Box, Button, Grid, Tab, Tabs, Typography } from '@mui/material';
import { useState, useEffect, createContext, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { RegisterForm } from '../../components/auth/RegisterForm';
import { ReactComponent as Logo } from '../../logo.svg';
import { HostOnboardingProvider } from '../../context/HostOnboardingContext';
import {
  HostOnboardingStep1,
  HostOnboardingStep2,
  HostOnboardingStep3,
  HostOnboardingStep4,
  HostOnboardingSuccess
} from '../../pages/host';

// Context to provide custom navigation for embedded mode
interface EmbeddedNavigationContextType {
  navigate: (path: string) => void;
}

const EmbeddedNavigationContext = createContext<EmbeddedNavigationContextType | null>(null);

// Wrapper component to handle step navigation within the tab using state
interface HostOnboardingFlowProps {
  currentStep: number;
  onStepChange: (step: number) => void;
}

const HostOnboardingFlow = ({ currentStep, onStepChange }: HostOnboardingFlowProps) => {
  const navigate = useNavigate();

  // Create a custom navigate function that intercepts onboarding routes
  const embeddedNavigate = (path: string) => {
    if (path === '/host/onboarding/step-1') {
      onStepChange(1);
    } else if (path === '/host/onboarding/step-2') {
      onStepChange(2);
    } else if (path === '/host/onboarding/step-3') {
      onStepChange(3);
    } else if (path === '/host/onboarding/step-4') {
      onStepChange(4);
    } else if (path === '/host/onboarding/success') {
      onStepChange(5);
    } else {
      // For other routes, use actual navigation
      navigate(path);
    }
  };

  // Intercept React Router navigation by patching history API
  useEffect(() => {
    // Store original methods
    const originalPushState = window.history.pushState.bind(window.history);
    const originalReplaceState = window.history.replaceState.bind(window.history);
    
    // Helper to extract path from URL
    const getPath = (url: string | URL | null | undefined): string => {
      if (!url) return window.location.pathname;
      if (typeof url === 'string') {
        try {
          const urlObj = new URL(url, window.location.origin);
          return urlObj.pathname;
        } catch {
          // If it's a relative path, combine with current origin
          return url.startsWith('/') ? url : `/${url}`;
        }
      }
      if (url instanceof URL) return url.pathname;
      return window.location.pathname;
    };
    
    // Override pushState to intercept navigation
    window.history.pushState = function(state, title, url) {
      const path = getPath(url as any);
      
      // Check if this is an onboarding route
      if (path.includes('/host/onboarding/step-1') || path === '/host/onboarding/step-1') {
        onStepChange(1);
        return; // Prevent navigation
      } else if (path.includes('/host/onboarding/step-2') || path === '/host/onboarding/step-2') {
        onStepChange(2);
        return;
      } else if (path.includes('/host/onboarding/step-3') || path === '/host/onboarding/step-3') {
        onStepChange(3);
        return;
      } else if (path.includes('/host/onboarding/step-4') || path === '/host/onboarding/step-4') {
        onStepChange(4);
        return;
      } else if (path.includes('/host/onboarding/success') || path === '/host/onboarding/success') {
        onStepChange(5);
        return;
      }
      
      // For other routes, use original pushState
      originalPushState(state, title, url);
    };

    // Override replaceState similarly
    window.history.replaceState = function(state, title, url) {
      const path = getPath(url as any);
      
      if (path.includes('/host/onboarding/step-1') || path === '/host/onboarding/step-1') {
        onStepChange(1);
        return;
      } else if (path.includes('/host/onboarding/step-2') || path === '/host/onboarding/step-2') {
        onStepChange(2);
        return;
      } else if (path.includes('/host/onboarding/step-3') || path === '/host/onboarding/step-3') {
        onStepChange(3);
        return;
      } else if (path.includes('/host/onboarding/step-4') || path === '/host/onboarding/step-4') {
        onStepChange(4);
        return;
      } else if (path.includes('/host/onboarding/success') || path === '/host/onboarding/success') {
        onStepChange(5);
        return;
      }
      
      originalReplaceState(state, title, url);
    };

    // Also monitor location changes as a fallback
    let lastPath = window.location.pathname;
    const checkLocation = () => {
      const currentPath = window.location.pathname;
      if (currentPath !== lastPath) {
        lastPath = currentPath;
        
        // If we navigated to an onboarding route, intercept it
        if (currentPath.includes('/host/onboarding/step-1')) {
          onStepChange(1);
          window.history.replaceState(null, '', '/register');
        } else if (currentPath.includes('/host/onboarding/step-2')) {
          onStepChange(2);
          window.history.replaceState(null, '', '/register');
        } else if (currentPath.includes('/host/onboarding/step-3')) {
          onStepChange(3);
          window.history.replaceState(null, '', '/register');
        } else if (currentPath.includes('/host/onboarding/step-4')) {
          onStepChange(4);
          window.history.replaceState(null, '', '/register');
        } else if (currentPath.includes('/host/onboarding/success')) {
          onStepChange(5);
          window.history.replaceState(null, '', '/register');
        }
      }
    };
    
    // Check frequently to catch any navigation that slips through
    const interval = setInterval(checkLocation, 50);
    
    // Also listen to popstate
    window.addEventListener('popstate', checkLocation);

    return () => {
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
      clearInterval(interval);
      window.removeEventListener('popstate', checkLocation);
    };
  }, [onStepChange]);

  // Render the appropriate step component based on currentStep
  return (
    <EmbeddedNavigationContext.Provider value={{ navigate: embeddedNavigate }}>
      {currentStep === 1 && <HostOnboardingStep1Wrapper onStepChange={onStepChange} />}
      {currentStep === 2 && <HostOnboardingStep2Wrapper onStepChange={onStepChange} />}
      {currentStep === 3 && <HostOnboardingStep3Wrapper onStepChange={onStepChange} />}
      {currentStep === 4 && <HostOnboardingStep4Wrapper onStepChange={onStepChange} />}
      {currentStep === 5 && <HostOnboardingSuccessWrapper />}
    </EmbeddedNavigationContext.Provider>
  );
};

// Wrapper components - navigation is intercepted at the HostOnboardingFlow level
const HostOnboardingStep1Wrapper = ({ onStepChange }: { onStepChange: (step: number) => void }) => {
  return <HostOnboardingStep1 />;
};

const HostOnboardingStep2Wrapper = ({ onStepChange }: { onStepChange: (step: number) => void }) => {
  return <HostOnboardingStep2 />;
};

const HostOnboardingStep3Wrapper = ({ onStepChange }: { onStepChange: (step: number) => void }) => {
  return <HostOnboardingStep3 />;
};

const HostOnboardingStep4Wrapper = ({ onStepChange }: { onStepChange: (step: number) => void }) => {
  return <HostOnboardingStep4 />;
};

const HostOnboardingSuccessWrapper = () => {
  return <HostOnboardingSuccess />;
};

const RootContainer = styled(Box)`
  // min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #181e25;
  padding: 1rem;
  @media (min-width: 600px) {
    padding: 2rem;
  }
`;

const BrandingBox = styled(Box)`
  background: #377dff;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 24px 0 0 24px;
  @media (max-width: 900px) {
    border-radius: 24px 24px 0 0;
    min-height: 150px;
    padding: 24px 0;
  }
  @media (max-width: 600px) {
    min-height: 120px;
    padding: 16px 0;
  }
`;

const FormBox = styled(Box)`
  background: #232933;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 48px 32px;
  border-radius: 0 24px 24px 0;
  @media (max-width: 900px) {
    border-radius: 0 0 24px 24px;
    padding: 32px 16px;
  }
  @media (max-width: 600px) {
    padding: 24px 16px;
  }
`;

const Card = styled(Box)`
  width: 100%;
  max-width: 100%;
  border-radius: 24px;
  // box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.37);
  overflow: hidden;
  background: #232933;
`;

const RegisterPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [hostOnboardingStep, setHostOnboardingStep] = useState(1);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    // Reset to step 1 when switching to host tab
    if (newValue === 1) {
      setHostOnboardingStep(1);
    }
  };

  const handleStepChange = (step: number) => {
    setHostOnboardingStep(step);
  };

  return (
    <RootContainer>
      <Card>
        <Grid container sx={{ minHeight: { xs: 'auto', md: 600 }, p:"25px" }}>
          <Grid size={{xs:12, md:4}}>
            <BrandingBox>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Logo style={{ width: 'auto', height: 'auto', maxWidth: '200px', maxHeight: '60px' }} />
              </Box>
            </BrandingBox>
          </Grid>
          <Grid size={{xs:12, md:8}}>
            <FormBox>
              <Typography variant="h4" sx={{ color: '#fff', fontWeight: 700, mb: 1, fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
                Sign Up
              </Typography>
              <Box sx={{ mb: 3, color: '#b0b8c1', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                Have an account?{' '}
                <Button
                  onClick={() => navigate('/login')}
                  sx={{ color: '#377dff', textTransform: 'none', fontWeight: 600, fontSize: { xs: 14, sm: 16 }, p: 0, minWidth: 0 }}
                >
                  Sign In
                </Button>
              </Box>
              
              <Box sx={{ mb: 3 }}>
                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  sx={{
                    '& .MuiTabs-indicator': {
                      backgroundColor: '#377dff',
                    },
                    '& .MuiTab-root': {
                      color: '#b0b8c1',
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      '&.Mui-selected': {
                        color: '#377dff',
                      },
                    },
                  }}
                >
                  <Tab label="Register as a Client" />
                  <Tab label="Become a Host Partner" />
                </Tabs>
              </Box>

              {activeTab === 0 && <RegisterForm />}
              {activeTab === 1 && (
                <HostOnboardingProvider>
                  <HostOnboardingFlow 
                    currentStep={hostOnboardingStep} 
                    onStepChange={handleStepChange}
                  />
                </HostOnboardingProvider>
              )}
            </FormBox>
          </Grid>
        </Grid>
      </Card>
    </RootContainer>
  );
};

export default RegisterPage; 